"use client";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useTranslations } from "next-intl";
import { useDropzone } from "react-dropzone";
import toast from "react-hot-toast";

import { useFormImageQueue } from "@/hooks/useFormImageQueue";
// Import SVG icons
import PhotoUploadIcon from "@/icons/icon_photo_upload.svg";
import PhotoUploadIconRed from "@/icons/icon_photo_upload_red.svg";
import PhotoUploadSmallIcon from "@/icons/icon_photo_upload_small.svg";
import PhotoUploadSmallIconRed from "@/icons/icon_photo_upload_small_red.svg";
// Import photo control icons
import CropIconRed from "@/icons/photo-controls/crop-red.svg";
import CropIconWhite from "@/icons/photo-controls/crop-white.svg";
import DeleteIconRed from "@/icons/photo-controls/delete-red.svg";
import DeleteIconWhite from "@/icons/photo-controls/delete-white.svg";
import RotateIconRed from "@/icons/photo-controls/rotate-red.svg";
import RotateIconWhite from "@/icons/photo-controls/rotate-white.svg";
import StarIconRed from "@/icons/photo-controls/star-red.svg";
import StarIconWhite from "@/icons/photo-controls/star-white.svg";
import ViewIconRed from "@/icons/photo-controls/view-red.svg";
import ViewIconWhite from "@/icons/photo-controls/view-white.svg";
import { imageUploadService } from "@/services/image-upload.service";
import Modal from "@/ui/Common/Modal";
import OptimizedImage from "@/ui/Common/OptimizedImage";
import { cn } from "@/utils/cn";
import { createLogger } from "@/utils/logger";

import { CropModal } from "../CropModal";

import styles from "./styles.module.scss";

// Import arrow icons

// Import components

const logger = createLogger("form-photo-upload");

// Utility function to fetch image through proxy to avoid CORS issues
const fetchImageThroughProxy = async (imageUrl: string): Promise<Blob> => {
  // Check if URL needs proxying (Google Storage URLs)
  if (imageUrl.startsWith("https://storage.googleapis.com/avezor-images/")) {
    const proxyUrl = `/api/proxy-image?url=${encodeURIComponent(imageUrl)}`;
    const response = await fetch(proxyUrl);
    if (!response.ok) {
      throw new Error(`Proxy request failed: ${response.status}`);
    }
    return await response.blob();
  }

  // For blob URLs that might be revoked, wrap in try/catch
  if (imageUrl.startsWith("blob:")) {
    try {
      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error(`Blob fetch failed: ${response.status}`);
      }
      return await response.blob();
    } catch (error) {
      throw new Error(`Blob URL is no longer valid or accessible: ${error}`);
    }
  }

  // For other URLs (data:), fetch directly
  const response = await fetch(imageUrl);
  if (!response.ok) {
    throw new Error(`Direct fetch failed: ${response.status}`);
  }
  return await response.blob();
};

export interface PhotoItem {
  id: string;
  uuid?: string;
  file?: File;
  preview: string;
  blobPreview?: string; // Original blob URL for smooth transition
  isMain?: boolean;
  // Additional fields for serialized data
  fileName?: string;
  fileType?: string;
  fileSize?: number;
  fileData?: string; // base64
  // New fields for immediate upload
  tempId?: string; // Server temp ID
  url?: string; // Server URL
  uploadStatus?: "pending" | "uploading" | "success" | "error";
  uploadProgress?: number;
  error?: string;
}

// Interface for serialized photo
export interface SerializedPhotoItem {
  id: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  fileData: string; // base64
  isMain?: boolean;
}

export interface FormPhotoUploadProps {
  label?: string;
  description?: string;
  value?: PhotoItem[];
  onChange: (photos: PhotoItem[]) => void;
  error?: string;
  required?: boolean;
  maxPhotos?: number;
  acceptedTypes?: string;
  mainPhotoLabel?: string;
  additionalPhotoLabel?: string;
  placeholdersCount?: number;
  autoSerialize?: boolean; // Automatically serialize on upload
  mainPhotoSize?: { width: number; height: number }; // Preview dimensions
  additionalPhotoSize?: { width: number; height: number }; // Preview dimensions
  className?: string;
  useFormImageQueueHook?: any;
}

// Проверка, выполняется ли код в браузере
const isBrowser = typeof window !== "undefined" && typeof File !== "undefined";

// Global cache for blob URLs to prevent recreation and flickering
const blobUrlCache = new Map<string, string>();
// Cache for processed photos to prevent re-processing
const photoCache = new Map<string, PhotoItem>();
// Set to track active blob URLs for cleanup
const activeBlobUrls = new Set<string>();

/**
 * Create and track blob URL for cleanup
 */
const createTrackedBlobUrl = (blob: Blob): string => {
  const url = URL.createObjectURL(blob);
  activeBlobUrls.add(url);
  return url;
};

/**
 * Revoke blob URL and remove from tracking
 */
const revokeTrackedBlobUrl = (url: string): void => {
  if (url.startsWith("blob:") && activeBlobUrls.has(url)) {
    URL.revokeObjectURL(url);
    activeBlobUrls.delete(url);
  }
};

/**
 * Clean up blob URLs for a photo
 */
const cleanupPhotoUrls = (photo: PhotoItem): void => {
  if (photo.preview && photo.preview.startsWith("blob:")) {
    revokeTrackedBlobUrl(photo.preview);
  }
  if (photo.blobPreview && photo.blobPreview.startsWith("blob:")) {
    revokeTrackedBlobUrl(photo.blobPreview);
  }
};

/**
 * Clean up all tracked blob URLs
 */
const cleanupAllBlobUrls = (): void => {
  activeBlobUrls.forEach((url) => {
    URL.revokeObjectURL(url);
  });
  activeBlobUrls.clear();
  blobUrlCache.clear();
  photoCache.clear();
};

/**
 * Prepare photos for display with comprehensive caching to prevent flickering
 */
const preparePhotosForDisplay = (photos: PhotoItem[]): PhotoItem[] => {
  if (!Array.isArray(photos)) {
    return [];
  }

  return photos.map((photo) => {
    // Если код выполняется на сервере, просто возвращаем фото без обработки
    if (!isBrowser) {
      return photo;
    }

    // Create a stable cache key for this photo - exclude uploadProgress to prevent cache misses
    const cacheKey = `${photo.id}:${photo.uploadStatus || "none"}:${photo.url || "none"}:${photo.isMain || false}`;

    // Check if we have a cached processed version
    const cachedPhoto = photoCache.get(cacheKey);
    if (cachedPhoto) {
      // Return the exact same object reference to prevent re-renders
      return cachedPhoto;
    }

    let processedPhoto: PhotoItem;

    // For uploaded photos, keep blob preview for smooth transition
    if (photo?.url && photo.uploadStatus === "success") {
      // Don't change preview, keep original blob for smooth transition
      processedPhoto = {
        ...photo,
        blobPreview: photo.preview, // Save blob URL for transition
        // Keep original preview URL (blob), don't switch to server URL
      };
    }
    // If this is serialized data (from localStorage) and needs blob URL creation
    else if (
      photo?.fileData &&
      !photo.preview.startsWith("blob:") &&
      !photo.url
    ) {
      // Check if we already have a cached blob URL for this photo
      const blobCacheKey = `blob_${photo.id}_${photo.fileData.slice(-50)}`;
      let cachedBlobUrl = blobUrlCache.get(blobCacheKey);

      if (!cachedBlobUrl) {
        try {
          // Create a blob from the fileData and generate a new preview URL
          const byteString = atob(photo.fileData.split(",")[1]);
          const mimeString = photo.fileData
            .split(",")[0]
            .split(":")[1]
            .split(";")[0];
          const ab = new ArrayBuffer(byteString.length);
          const ia = new Uint8Array(ab);

          for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
          }

          const blob = new Blob([ab], { type: mimeString });
          cachedBlobUrl = createTrackedBlobUrl(blob);

          // Cache the URL to prevent recreation
          blobUrlCache.set(blobCacheKey, cachedBlobUrl);
        } catch (e) {
          console.error("Error creating blob URL from fileData", e);
          cachedBlobUrl = photo.preview; // Fallback to original preview
        }
      }

      // Only create new object if preview needs to change
      if (photo.preview !== cachedBlobUrl) {
        processedPhoto = {
          ...photo,
          preview: cachedBlobUrl,
          blobPreview: cachedBlobUrl, // Same as preview for serialized data
        };
      } else {
        processedPhoto = {
          ...photo,
          blobPreview: photo.preview, // Set blobPreview for consistency
        };
      }
    }
    // For photos that don't need processing, return original object
    else {
      processedPhoto = {
        ...photo,
        blobPreview: photo.preview, // Set blobPreview for consistency
      };
    }

    // Cache the processed photo - don't freeze to allow React optimizations
    photoCache.set(cacheKey, processedPhoto);

    return processedPhoto;
  });
};

/**
 * Component for uploading and displaying photos
 */
const FormPhotoUpload: React.FC<FormPhotoUploadProps> = ({
  label,
  description,
  value = [],
  onChange,
  error,
  required = false,
  maxPhotos = 17,
  acceptedTypes = "image/*,video/*,application/pdf",
  mainPhotoLabel = "+ Upload photo",
  additionalPhotoLabel = "+ Upload photo",
  placeholdersCount = 6,
  autoSerialize = false,
  mainPhotoSize,
  additionalPhotoSize,
  className,
  useFormImageQueueHook,
}) => {
  // Get translations
  const t = useTranslations();

  // Create lightweight hash for value comparison - exclude uploadProgress to prevent unnecessary recalculations
  const valueHash = useMemo(() => {
    if (!value) {
      return "";
    }
    // Exclude uploadProgress as it changes frequently but doesn't affect photo structure
    const hash = value
      .map((p) => `${p.id}:${p.uploadStatus}:${p.url}:${p.isMain}`)
      .join("|");
    console.log("[FormPhotoUpload] valueHash recalculated:", hash);
    return hash;
  }, [value]);

  // Stable memoized photos to prevent flickering
  const displayPhotos = useMemo(() => {
    console.log("[FormPhotoUpload] Recalculating displayPhotos"); // Add log
    return preparePhotosForDisplay(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueHash]);

  // Debug: Track component lifecycle
  useEffect(() => {
    console.log("🔥 DEBUG: FormPhotoUpload component mounted");
    return () => {
      console.log("🔥 DEBUG: FormPhotoUpload component unmounting");
    };
  }, []);

  // Find main photo and additional photos with memoization
  const mainPhoto = useMemo(() => {
    console.log("[FormPhotoUpload] Recalculating mainPhoto"); // Add log
    const found = displayPhotos.find((photo) => photo.isMain);
    console.log(
      "[FormPhotoUpload] mainPhoto result:",
      found?.id,
      found?.uploadStatus,
      found?.uploadProgress,
    );
    return found;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayPhotos]);

  // Use a more stable reference for mainPhoto to prevent unnecessary re-renders
  const stableMainPhoto = useMemo(() => {
    if (!mainPhoto) {
      return undefined;
    }

    // Create a stable object that only changes when important properties change
    return {
      id: mainPhoto.id,
      uploadStatus: mainPhoto.uploadStatus,
      url: mainPhoto.url,
      isMain: mainPhoto.isMain,
      preview: mainPhoto.preview,
      // Include other properties that might be needed
      fileName: mainPhoto.fileName,
      fileType: mainPhoto.fileType,
      fileSize: mainPhoto.fileSize,
      tempId: mainPhoto.tempId,
      error: mainPhoto.error,
      // Exclude uploadProgress as it changes frequently
      file: mainPhoto.file,
      fileData: mainPhoto.fileData,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mainPhoto?.id,
    mainPhoto?.uploadStatus,
    mainPhoto?.url,
    mainPhoto?.isMain,
    mainPhoto?.preview,
    mainPhoto?.fileName,
    mainPhoto?.fileType,
    mainPhoto?.fileSize,
    mainPhoto?.tempId,
    mainPhoto?.error,
    mainPhoto?.file,
    mainPhoto?.fileData,
    // Exclude uploadProgress from dependencies
  ]);

  const additionalPhotos = useMemo(() => {
    console.log("[FormPhotoUpload] Recalculating additionalPhotos"); // Add log
    return displayPhotos.filter((photo) => !photo.isMain);
  }, [displayPhotos]);

  // Memoize sortable item IDs to prevent re-renders
  const mainPhotoSortableItems = useMemo(() => {
    const items = stableMainPhoto ? [stableMainPhoto.id] : [];
    console.log(
      "[FormPhotoUpload] mainPhotoSortableItems recalculated:",
      items,
    );
    return items;
  }, [stableMainPhoto]);

  const additionalPhotosIdsString = useMemo(() => {
    return additionalPhotos.map((p) => p.id).join(",");
  }, [additionalPhotos]);

  const additionalPhotosSortableItems = useMemo(() => {
    const items = additionalPhotos.map((photo) => photo.id);
    console.log(
      "[FormPhotoUpload] additionalPhotosSortableItems recalculated:",
      items,
    );
    return items;
    // Use a string representation of IDs for dependency to stabilize the array reference
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [additionalPhotosIdsString]);

  const extraPhotosSlice = useMemo(() => {
    return additionalPhotos.slice(placeholdersCount);
  }, [additionalPhotos, placeholdersCount]);

  const extraPhotosIdsString = useMemo(() => {
    return extraPhotosSlice.map((p) => p.id).join(",");
  }, [extraPhotosSlice]);

  const extraPhotosSortableItems = useMemo(() => {
    const items = extraPhotosSlice.map((photo) => photo.id);
    console.log(
      "[FormPhotoUpload] extraPhotosSortableItems recalculated:",
      items,
    );
    return items;
    // Use a string representation of IDs and placeholdersCount for dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extraPhotosIdsString, placeholdersCount]);

  // Additional effect to clear cache when photos change significantly
  useEffect(() => {
    // Clear cache entries for photos that no longer exist
    const currentPhotoIds = new Set(value?.map((p) => p.id) || []);

    for (const [key] of photoCache.entries()) {
      const photoId = key.split("_")[0];
      if (!currentPhotoIds.has(photoId)) {
        photoCache.delete(key);
      }
    }

    for (const [key] of blobUrlCache.entries()) {
      const photoId = key.split("_")[1]; // blob_photoId_...
      if (!currentPhotoIds.has(photoId)) {
        const url = blobUrlCache.get(key);
        if (url) {
          revokeTrackedBlobUrl(url);
        }
        blobUrlCache.delete(key);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueHash]); // Use valueHash instead of value to prevent unnecessary runs

  // Add ref to keep track of current photos state for async callbacks
  const photosRef = useRef<PhotoItem[]>(displayPhotos);
  useEffect(() => {
    photosRef.current = displayPhotos;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayPhotos]);

  // Enhanced cleanup blob URLs on unmount with new tracking system
  useEffect(() => {
    return () => {
      // Use new cleanup functions
      cleanupAllBlobUrls();

      // Clear caches
      blobUrlCache.clear();
      photoCache.clear();

      // Cleanup current photos blob URLs that might not be tracked
      displayPhotos.forEach((photo) => {
        cleanupPhotoUrls(photo);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array for unmount only

  // Refs for input elements
  const mainInputRef = useRef<HTMLInputElement>(null);
  const additionalInputRefs = useMemo(() => {
    return Array(Math.max(placeholdersCount, maxPhotos))
      .fill(0)
      .map(() => React.createRef<HTMLInputElement>());
  }, [placeholdersCount, maxPhotos]);

  // State for modal windows
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [imageToEdit, setImageToEdit] = useState<PhotoItem | null>(null);
  const [cropPhotoIndex, setCropPhotoIndex] = useState<number | undefined>(
    undefined,
  );
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [cropArea, setCropArea] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);

  // New state for image viewing
  const [viewImageUrl, setViewImageUrl] = useState<string | null>(null);

  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));

  // ------------------------------------------------------------
  // Global drag-and-drop safeguard (fix for Windows browsers)
  // ------------------------------------------------------------
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const hasFiles = (e: DragEvent): boolean => {
      return Array.from(e.dataTransfer?.types || []).includes("Files");
    };

    const preventDefaultIfFiles = (e: DragEvent): void => {
      if (hasFiles(e)) {
        e.preventDefault();
      }
    };

    // Use capture phase to intercept as early as possible
    const opts: AddEventListenerOptions = { capture: true };

    window.addEventListener("dragenter", preventDefaultIfFiles, opts);
    window.addEventListener("dragover", preventDefaultIfFiles, opts);
    window.addEventListener("drop", preventDefaultIfFiles, opts);

    return () => {
      window.removeEventListener("dragenter", preventDefaultIfFiles, opts);
      window.removeEventListener("dragover", preventDefaultIfFiles, opts);
      window.removeEventListener("drop", preventDefaultIfFiles, opts);
    };
  }, []);

  // Memoize strategy to prevent re-renders
  const sortingStrategy = useMemo(() => horizontalListSortingStrategy, []);

  // Debug wrapper for SortableContext to track re-renders
  const LoggedSortableContext: React.FC<{
    items: string[];
    strategy: any;
    children: React.ReactNode;
    contextName: string;
  }> = React.memo(
    ({ items, strategy, children, contextName }) => {
      console.log(
        `[${contextName}] SortableContext rendering with items:`,
        items,
      );

      return (
        <SortableContext
          items={items}
          strategy={strategy}
        >
          {children}
        </SortableContext>
      );
    },
    (prevProps, nextProps) => {
      // Custom comparison for items array: shallow compare elements
      let itemsChanged = prevProps.items.length !== nextProps.items.length;
      if (!itemsChanged) {
        for (let i = 0; i < prevProps.items.length; i++) {
          if (prevProps.items[i] !== nextProps.items[i]) {
            itemsChanged = true;
            break;
          }
        }
      }

      const strategyChanged = prevProps.strategy !== nextProps.strategy;

      console.log(
        `[${prevProps.contextName}] SortableContext memo comparison:`,
        {
          itemsChanged,
          strategyChanged,
          willRerender: itemsChanged || strategyChanged,
          prevItemsLength: prevProps.items.length, // Changed to prevItemsLength for clarity
          nextItemsLength: nextProps.items.length, // Changed to nextItemsLength for clarity
        },
      );

      return !itemsChanged && !strategyChanged;
    },
  );

  LoggedSortableContext.displayName = "LoggedSortableContext";

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  }, []); // Empty dependency array as setActiveId is stable

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (active.id !== over?.id) {
        const oldIndex = photosRef.current.findIndex(
          (photo) => photo.id === active.id,
        );
        const newIndex = photosRef.current.findIndex(
          (photo) => photo.id === over?.id,
        );

        if (oldIndex !== -1 && newIndex !== -1) {
          const newPhotos = arrayMove(photosRef.current, oldIndex, newIndex);

          // Update isMain for first photo
          const updatedPhotos = newPhotos.map((photo, index) => ({
            ...photo,
            isMain: index === 0,
            isPrimary: index === 0,
          }));

          onChange(updatedPhotos);
        }
      }

      setActiveId(null);
    },
    [onChange],
  ); // Include onChange as it's an external prop

  // Add functions for photo processing
  const viewPhoto = useCallback(
    (photoId: string) => {
      const photo = displayPhotos.find((p) => p.id === photoId);
      if (photo) {
        setViewImageUrl(photo.preview);
      }
    },
    [displayPhotos],
  );

  const commitPhotos = (updater: (prev: PhotoItem[]) => PhotoItem[]) => {
    const next = updater(photosRef.current);
    photosRef.current = next;
    onChange(next);
  };

  const rotatePhoto = useCallback(
    async (photoId: string) => {
      const photo = displayPhotos.find((p) => p.id === photoId);
      if (!photo) {
        return;
      }

      console.log("[rotatePhoto] Starting with autoSerialize:", autoSerialize);

      try {
        let blob: Blob;

        // Try to use file first, then fileData, then preview URL as fallback
        if (photo.file) {
          blob = photo.file;
        } else if (photo.fileData) {
          // Convert base64 to blob
          const binaryString = atob(photo.fileData.split(",")[1]);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          blob = new Blob([bytes], { type: photo.fileType || "image/jpeg" });
        } else {
          // Fallback to fetching from preview URL
          blob = await fetchImageThroughProxy(photo.preview);
        }

        const imageUrl = URL.createObjectURL(blob);

        // Create temporary canvas for image rotation
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          return;
        }

        // Create temporary image with crossOrigin
        const img = document.createElement("img");
        img.crossOrigin = "anonymous"; // This helps avoid tainted canvas
        img.src = imageUrl;

        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
        });

        // Swap dimensions for rotation
        canvas.width = img.height;
        canvas.height = img.width;

        // Rotate 90 degrees clockwise
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate(Math.PI / 2);
        ctx.drawImage(img, -img.width / 2, -img.height / 2);

        // Clean up temporary URL
        URL.revokeObjectURL(imageUrl);

        // Create new file from canvas
        const rotatedBlob = await new Promise<Blob>((resolve) =>
          canvas.toBlob((blob) => resolve(blob as Blob), "image/jpeg", 0.95),
        );
        const rotatedFile = new File(
          [rotatedBlob],
          photo.fileName || "rotated.jpg",
          {
            type: "image/jpeg",
          },
        );

        // Create new URL for preview
        const preview = URL.createObjectURL(rotatedBlob);

        // Free old URL only if it's a blob URL
        if (photo.preview.startsWith("blob:")) {
          URL.revokeObjectURL(photo.preview);
        }

        let updatedPhoto: PhotoItem;
        if (autoSerialize) {
          console.log("[rotatePhoto] autoSerialize=true, processing fileData");
          const fileData = await readFileAsBase64(rotatedFile);
          updatedPhoto = {
            ...photo,
            file: rotatedFile,
            fileName: rotatedFile.name,
            fileType: rotatedFile.type,
            fileSize: rotatedFile.size,
            fileData,
            preview,
            blobPreview: preview, // Set blobPreview for consistency
          };

          // Update photos in autoSerialize mode
          const updatedPhotos = displayPhotos.map((p) =>
            p.id === photoId ? updatedPhoto : p,
          );
          console.log(
            "[rotatePhoto] autoSerialize onChange called, updatedPhoto:",
            {
              id: updatedPhoto.id,
              preview: updatedPhoto.preview,
              hasFileData: !!updatedPhoto.fileData,
            },
          );
          onChange(updatedPhotos);
        } else {
          // Use immediate upload for rotated image
          updatedPhoto = {
            ...photo,
            file: rotatedFile,
            preview,
            blobPreview: preview, // Set blobPreview for rotated image
            uploadStatus: "uploading" as const,
            uploadProgress: 0,
          };

          // Update photo list with uploading status first
          const photosWithUploading = displayPhotos.map((p) =>
            p.id === photoId ? updatedPhoto : p,
          );
          onChange(photosWithUploading);

          // Start immediate upload of rotated image
          try {
            // Update with uploading status first
            onChange(photosWithUploading);

            await imageUploadService.uploadImage(rotatedFile, {
              onProgress: (progress) => {
                // Get current photos from value prop and update with progress
                const currentPhotos = value || [];
                const progressPhotos = currentPhotos.map(
                  (currentPhoto: PhotoItem) =>
                    currentPhoto.id === photoId
                      ? { ...currentPhoto, uploadProgress: progress.percentage }
                      : currentPhoto,
                );
                onChange(progressPhotos);
              },
              onSuccess: (result) => {
                // Get current photos from value prop and update with success
                const currentPhotos = value || [];

                // Create URL for the new server image
                const newPreviewUrl = result.url;

                // Free old blob URL
                if (preview.startsWith("blob:")) {
                  URL.revokeObjectURL(preview);
                }

                commitPhotos((prev) =>
                  prev.map((p) =>
                    p.id === photoId
                      ? {
                          ...p,
                          tempId: result.uuid,
                          uuid: result.uuid,
                          url: result.url,
                          preview: newPreviewUrl, // Use server URL as preview
                          uploadStatus: "success" as const,
                          uploadProgress: 100,
                          // Keep the rotated file for potential future operations
                          file: rotatedFile,
                          fileName: rotatedFile.name,
                          fileType: rotatedFile.type,
                          fileSize: rotatedFile.size,
                          // Clear old blobPreview since we have server URL now
                          blobPreview: undefined,
                        }
                      : p,
                  ),
                );
              },
              onError: (error) => {
                // Use photosRef to get current state and update
                const currentPhotos = photosRef.current;
                const failedPhoto = currentPhotos.find((p) => p.id === photoId);
                if (failedPhoto) {
                  cleanupPhotoUrls(failedPhoto);
                }
                const filtered = currentPhotos.filter((p) => p.id !== photoId);
                onChange(filtered);
                const errMsg =
                  typeof error === "object" &&
                  error &&
                  "message" in (error as any)
                    ? (error as any).message
                    : "Upload failed";
                toast.error(t(errMsg));
              },
            });
          } catch (uploadError) {
            // Handle upload error
            const errorPhotos = displayPhotos.map((p) =>
              p.id === photoId
                ? {
                    ...updatedPhoto,
                    uploadStatus: "error" as const,
                    error:
                      uploadError instanceof Error
                        ? uploadError.message
                        : "Upload failed",
                  }
                : p,
            );
            onChange(errorPhotos);
          }
        }
      } catch (error) {
        console.error("Error rotating image:", error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [displayPhotos, onChange, autoSerialize],
  );

  const cropPhoto = useCallback(
    (photoId: string) => {
      const photoIndex = displayPhotos.findIndex((p) => p.id === photoId);
      const photo = displayPhotos.find((p) => p.id === photoId);
      if (photo && photoIndex !== -1) {
        setImageToEdit(photo);
        setCropPhotoIndex(photoIndex); // Устанавливаем индекс для замены
        setCropModalOpen(true);
      }
    },
    [displayPhotos],
  );

  const removePhoto = useCallback(
    (photoId: string) => {
      logger.info(`Removing photo with id: ${photoId}`);

      // Find the photo being removed to cleanup its URLs properly
      const photoToRemove = displayPhotos.find((p) => p.id === photoId);
      if (photoToRemove) {
        cleanupPhotoUrls(photoToRemove);
      }

      // Remove from blob URL cache - clean all entries for this photo
      for (const [key, url] of blobUrlCache.entries()) {
        if (key.includes(photoId) || url === photoToRemove?.preview) {
          revokeTrackedBlobUrl(url);
          blobUrlCache.delete(key);
        }
      }

      // Remove from photo cache - remove all entries for this photo ID
      for (const [key] of photoCache.entries()) {
        if (key.startsWith(`${photoId}_`)) {
          photoCache.delete(key);
        }
      }

      const updatedPhotos = displayPhotos.filter((p) => p.id !== photoId);

      // If main photo was deleted, make first remaining photo main
      if (updatedPhotos.length > 0) {
        const wasMain = displayPhotos.find((p) => p.id === photoId)?.isMain;
        if (wasMain) {
          updatedPhotos[0].isMain = true;
        }
      }

      onChange(updatedPhotos);
    },
    [displayPhotos, onChange],
  );

  // Update onDrop handler for proper handling of multiple files
  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      console.log("🔥 DEBUG: onDrop CALLED!", acceptedFiles.length, "files");
      console.log(
        "🎯 onDrop called with files:",
        acceptedFiles.map((f) => ({ name: f.name, size: f.size })),
      );
      logger.info("Files dropped:", acceptedFiles);

      // Check if we exceed maximum number of photos
      const totalPhotos = [...displayPhotos, ...acceptedFiles];
      if (totalPhotos.length > maxPhotos) {
        console.log("[onDrop] MaximumPhotosExceeded", {
          totalPhotos: totalPhotos.length,
          maxPhotos,
          displayPhotos: displayPhotos.length,
          acceptedFiles: acceptedFiles.length,
        });
        toast.error(t("MaximumPhotosExceeded", { count: maxPhotos }));
        return;
      }

      const newPhotos: PhotoItem[] = [];

      // Process each file properly like in handleAdditionalPhotoUpload
      for (let i = 0; i < acceptedFiles.length; i++) {
        const file = acceptedFiles[i];
        const photoId = `photo_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
        const blobUrl = createTrackedBlobUrl(file);

        // Determine if this should be main photo (first photo and no existing main photo)
        const shouldBeMain = displayPhotos.length === 0 && i === 0;

        const commonFileProperties = {
          id: photoId,
          file, // Always include the file object
          preview: blobUrl,
          blobPreview: blobUrl, // Set blobPreview for smooth transition
          isMain: shouldBeMain,
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
        };

        if (autoSerialize) {
          const fileData = await readFileAsBase64(file);
          newPhotos.push({
            ...commonFileProperties,
            fileData,
          });
        } else {
          // Create photo with uploading status
          const photoWithUpload: PhotoItem = {
            ...commonFileProperties,
            uploadStatus: "uploading" as const,
            uploadProgress: 0,
          };

          newPhotos.push(photoWithUpload);
        }
      }

      // If we have a main photo already, make sure new photos are not main
      const hasMainPhoto = displayPhotos.some((photo) => photo.isMain);
      if (hasMainPhoto) {
        newPhotos.forEach((photo) => {
          photo.isMain = false;
        });
      }

      // Add all photos to state first
      const updatedPhotos = [...displayPhotos, ...newPhotos];
      onChange(updatedPhotos);

      // Start uploads after adding photos to state (only if not autoSerialize)
      console.log("🔥 DEBUG: autoSerialize check:", {
        autoSerialize,
        filesCount: acceptedFiles.length,
      });
      if (!autoSerialize) {
        console.log("🔥 DEBUG: Starting uploads - autoSerialize is false");
        console.log(
          "🔥 DEBUG: imageUploadService available:",
          !!imageUploadService,
        );
        console.log(
          "🔥 DEBUG: imageUploadService.uploadImage available:",
          !!imageUploadService?.uploadImage,
        );

        for (let i = 0; i < acceptedFiles.length; i++) {
          const file = acceptedFiles[i];
          const photoId = newPhotos[i].id;

          console.log("🎯 Starting upload for file:", {
            filename: file.name,
            photoId,
          });
          console.log("🔥 DEBUG: About to call imageUploadService.uploadImage");

          try {
            const uploadResult = await imageUploadService.uploadImage(file, {
              onProgress: (progress) => {
                console.log("🔥 DEBUG: Upload progress callback:", {
                  photoId,
                  progress,
                });
                // Use photosRef to get current state and update
                const currentPhotos = photosRef.current;
                const updatedPhotos = currentPhotos.map((photo: PhotoItem) =>
                  photo.id === photoId
                    ? { ...photo, uploadProgress: progress.percentage }
                    : photo,
                );
                onChange(updatedPhotos);
              },
              onSuccess: (result) => {
                commitPhotos((prev) =>
                  prev.map((p) =>
                    p.id === photoId
                      ? {
                          ...p,
                          tempId: result.uuid,
                          uuid: result.uuid,
                          url: result.url,
                          uploadStatus: "success",
                          uploadProgress: 100,
                        }
                      : p,
                  ),
                );
              },
              onError: (error) => {
                console.log("🔥 DEBUG: Upload error callback:", {
                  photoId,
                  error,
                });

                // Use photosRef to get current state and update
                const currentPhotos = photosRef.current;
                const updatedPhotos = currentPhotos.map((photo: PhotoItem) =>
                  photo.id === photoId
                    ? {
                        ...photo,
                        uploadStatus: "error" as const,
                        error,
                      }
                    : photo,
                );
                onChange(updatedPhotos);
              },
            });

            console.log(
              "🔥 DEBUG: imageUploadService.uploadImage completed:",
              uploadResult,
            );
          } catch (error) {
            // Use photosRef to get current state and update
            const currentPhotos = photosRef.current;
            const updatedPhotos = currentPhotos.map((photo: PhotoItem) =>
              photo.id === photoId
                ? {
                    ...photo,
                    uploadStatus: "error" as const,
                    error:
                      error instanceof Error ? error.message : "Upload failed",
                  }
                : photo,
            );
            onChange(updatedPhotos);
            toast.error(
              t(error instanceof Error ? error.message : "Upload failed"),
            );
          }
        }
      }

      // Reset main input if available
      if (mainInputRef.current) {
        mainInputRef.current.value = "";
      }
    },
    [displayPhotos, onChange, maxPhotos, mainInputRef, autoSerialize, t],
  );

  // Handler for main photo upload
  const handleMainPhotoUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      console.log("🔥 DEBUG: handleMainPhotoUpload CALLED!");
      if (!e.target.files || e.target.files.length === 0) {
        console.log("🔥 DEBUG: No files selected");
        return;
      }

      const files = Array.from(e.target.files);
      console.log(
        "🔥 DEBUG: handleMainPhotoUpload files:",
        files.length,
        files.map((f) => f.name),
      );

      const newPhotos: PhotoItem[] = [];

      files.forEach((file, index) => {
        const photoId = `photo_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
        const blobUrl = createTrackedBlobUrl(file);

        const commonFileProperties = {
          id: photoId,
          file,
          preview: blobUrl,
          blobPreview: blobUrl,
          // First selected file becomes main, others regular
          isMain: index === 0,
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
        };

        if (autoSerialize) {
          // eslint-disable-next-line no-loop-func
          newPhotos.push({
            ...commonFileProperties,
            // We'll read fileData later asynchronously for all if needed
          });
        } else {
          newPhotos.push({
            ...commonFileProperties,
            uploadStatus: "uploading" as const,
            uploadProgress: 0,
          });
        }
      });

      // Demote existing main photo if any
      const existingDemoted = displayPhotos.map((p) => ({
        ...p,
        isMain: false,
      }));

      // Prepend new photos (maintain main first)
      const updatedPhotos = [...newPhotos, ...existingDemoted];
      onChange(updatedPhotos);

      // Reset input for possible re-upload of same file
      if (mainInputRef.current) {
        mainInputRef.current.value = "";
      }

      // Start uploads for each new photo if immediate upload mode
      console.log("🔥 DEBUG: handleMainPhotoUpload autoSerialize check:", {
        autoSerialize,
        filesCount: files.length,
      });
      console.log("🔥 DEBUG: About to check autoSerialize condition");
      if (!autoSerialize) {
        console.log(
          "🔥 DEBUG: autoSerialize is FALSE - entering upload section",
        );
        console.log(
          "🔥 DEBUG: Starting uploads in handleMainPhotoUpload - Promise.all",
        );
        console.log(
          "🔥 DEBUG: imageUploadService available:",
          !!imageUploadService,
        );
        console.log(
          "🔥 DEBUG: imageUploadService.uploadImage available:",
          !!imageUploadService?.uploadImage,
        );

        try {
          console.log("🔥 DEBUG: Entering Promise.all block");
          await Promise.all(
            files.map(async (file, idx) => {
              const photoId = newPhotos[idx].id;
              console.log("🔥 DEBUG: handleMainPhotoUpload - uploading file:", {
                filename: file.name,
                photoId,
              });
              console.log(
                "🔥 DEBUG: About to call imageUploadService.uploadImage from handleMainPhotoUpload",
              );

              try {
                const uploadResult = await imageUploadService.uploadImage(
                  file,
                  {
                    onProgress: (progress) => {
                      console.log(
                        "🔥 DEBUG: handleMainPhotoUpload progress callback:",
                        { photoId, progress },
                      );
                      const currentPhotos = photosRef.current;
                      const progressPhotos = currentPhotos.map(
                        (p: PhotoItem) =>
                          p.id === photoId
                            ? { ...p, uploadProgress: progress.percentage }
                            : p,
                      );
                      onChange(progressPhotos);
                    },
                    onSuccess: (result) => {
                      commitPhotos((prev) =>
                        prev.map((p) =>
                          p.id === photoId
                            ? {
                                ...p,
                                tempId: result.uuid,
                                uuid: result.uuid,
                                url: result.url,
                                uploadStatus: "success",
                                uploadProgress: 100,
                              }
                            : p,
                        ),
                      );
                    },
                    onError: (error) => {
                      console.log(
                        "🔥 DEBUG: handleMainPhotoUpload error callback:",
                        { photoId, error },
                      );
                      const currentPhotos = photosRef.current;
                      const failedPhoto = currentPhotos.find(
                        (p) => p.id === photoId,
                      );
                      if (failedPhoto) {
                        cleanupPhotoUrls(failedPhoto);
                      }
                      const filtered = currentPhotos.filter(
                        (p) => p.id !== photoId,
                      );
                      onChange(filtered);
                      toast.error(t(error));
                    },
                  },
                );

                console.log(
                  "🔥 DEBUG: handleMainPhotoUpload uploadResult:",
                  uploadResult,
                );
              } catch (err) {
                console.log(
                  "🔥 DEBUG: handleMainPhotoUpload threw exception:",
                  { photoId, err },
                );
                const currentPhotos = photosRef.current;
                const errorPhotos = currentPhotos.map((p: PhotoItem) =>
                  p.id === photoId
                    ? {
                        ...p,
                        uploadStatus: "error" as const,
                        error:
                          err instanceof Error ? err.message : "Upload failed",
                      }
                    : p,
                );
                onChange(errorPhotos);
              }
            }),
          );
          console.log("🔥 DEBUG: Promise.all completed successfully");
        } catch (promiseAllError) {
          console.log("🔥 DEBUG: Promise.all threw error:", promiseAllError);
        }
      } else {
        console.log("🔥 DEBUG: autoSerialize is TRUE - skipping uploads");
      }

      // If serialization is enabled, read fileData for new photos asynchronously
      if (autoSerialize) {
        const photosWithFileData = await Promise.all(
          newPhotos.map(async (photo) => {
            const fileData = await readFileAsBase64(photo.file!);
            return { ...photo, fileData };
          }),
        );
        // Update photos with fileData
        onChange(photosWithFileData);
      }
    },
    [displayPhotos, onChange, autoSerialize],
  );

  // Handler for additional photos upload
  const handleAdditionalPhotoUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!e.target.files || e.target.files.length === 0) {
        return;
      }

      const files = Array.from(e.target.files);
      const newPhotos: PhotoItem[] = [];

      // Process each file
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const photoId = `photo_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
        const blobUrl = createTrackedBlobUrl(file);

        const commonFileProperties = {
          id: photoId,
          file, // Always include the file object
          preview: blobUrl,
          blobPreview: blobUrl, // Set blobPreview for smooth transition
          isMain: displayPhotos.length === 0 && i === 0,
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
        };

        if (autoSerialize) {
          const fileData = await readFileAsBase64(file);
          newPhotos.push({
            ...commonFileProperties,
            fileData,
          });
        } else {
          // Create photo with uploading status
          const photoWithUpload: PhotoItem = {
            ...commonFileProperties,
            uploadStatus: "uploading" as const,
            uploadProgress: 0,
          };

          newPhotos.push(photoWithUpload);
        }
      }

      // Check if we exceed maximum number of photos
      const totalPhotos = [...displayPhotos, ...newPhotos];
      if (totalPhotos.length > maxPhotos) {
        toast.error(t("MaximumPhotosExceeded", { count: maxPhotos }));
        return;
      }

      // Add all photos to state first
      onChange([...displayPhotos, ...newPhotos]);

      // Reset input for possible re-upload of same files
      if (e.target) {
        e.target.value = "";
      }

      // Start uploads after adding photos to state (only if not autoSerialize)
      if (!autoSerialize) {
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const photoId = newPhotos[i].id;

          try {
            await imageUploadService.uploadImage(file, {
              onProgress: (progress) => {
                // Use photosRef to get current state and update
                const currentPhotos = photosRef.current;
                const updatedPhotos = currentPhotos.map((photo: PhotoItem) =>
                  photo.id === photoId
                    ? { ...photo, uploadProgress: progress.percentage }
                    : photo,
                );
                onChange(updatedPhotos);
              },
              onSuccess: (result) => {
                commitPhotos((prev) =>
                  prev.map((p) =>
                    p.id === photoId
                      ? {
                          ...p,
                          tempId: result.uuid,
                          uuid: result.uuid,
                          url: result.url,
                          uploadStatus: "success",
                          uploadProgress: 100,
                        }
                      : p,
                  ),
                );
              },
              onError: (error) => {
                // Use photosRef to get current state and update
                const currentPhotos = photosRef.current;
                const updatedPhotos = currentPhotos.map((photo: PhotoItem) =>
                  photo.id === photoId
                    ? {
                        ...photo,
                        uploadStatus: "error" as const,
                        error,
                      }
                    : photo,
                );
                onChange(updatedPhotos);
              },
            });
          } catch (error) {
            // Use photosRef to get current state and update
            const currentPhotos = photosRef.current;
            const updatedPhotos = currentPhotos.map((photo: PhotoItem) =>
              photo.id === photoId
                ? {
                    ...photo,
                    uploadStatus: "error" as const,
                    error:
                      error instanceof Error ? error.message : "Upload failed",
                  }
                : photo,
            );
            onChange(updatedPhotos);
            const errMsg =
              typeof error === "object" && error && "message" in (error as any)
                ? (error as any).message
                : "Upload failed";
            toast.error(t(errMsg));
          }
        }
      }
    },
    [displayPhotos, onChange, maxPhotos, t, autoSerialize],
  );

  // Функция для установки фото как главного
  const setAsMainPhoto = useCallback(
    (photoId: string) => {
      const photo = displayPhotos.find((p) => p.id === photoId);
      if (!photo) {
        return;
      }

      // Обновляем все фото, устанавливая isMain только для выбранного
      const updatedPhotos = displayPhotos
        .sort((a, b) => (a.id === photoId ? -1 : b.id === photoId ? 1 : 0))
        .map((p) => ({
          ...p,
          isMain: p.id === photoId,
          isPrimary: p.id === photoId,
        }));

      onChange(updatedPhotos);
    },
    [displayPhotos, onChange],
  );

  // Handle crop completion
  const handleCropComplete = useCallback(
    (croppedArea: any, croppedAreaPixels: any) => {
      setCropArea(croppedAreaPixels);
    },
    [],
  );

  // Apply crop to image
  const applyCrop = useCallback(
    async (customCropArea?: {
      x: number;
      y: number;
      width: number;
      height: number;
    }) => {
      const activeCropArea = customCropArea || cropArea;
      if (!imageToEdit || !activeCropArea) {
        return;
      }
      if (!isBrowser) {
        return;
      } // Проверка на браузерную среду

      console.log(
        "[applyCrop] Starting with autoSerialize:",
        autoSerialize,
        "cropArea:",
        activeCropArea,
      );

      try {
        let file = imageToEdit.file;

        // If no file but fileData exists, deserialize data
        if (!file && imageToEdit.fileData) {
          const binaryString = atob(imageToEdit.fileData.split(",")[1]);
          const bytes = new Uint8Array(binaryString.length);

          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }

          const blob = new Blob([bytes], { type: imageToEdit.fileType });
          file = new File([blob], imageToEdit.fileName || "photo.jpg", {
            type: imageToEdit.fileType,
            lastModified: Date.now(),
          });
        }

        // If still no file, try to fetch from preview URL (for uploaded images after page reload)
        if (!file && imageToEdit.preview) {
          try {
            const blob = await fetchImageThroughProxy(imageToEdit.preview);
            file = new File([blob], imageToEdit.fileName || "photo.jpg", {
              type: imageToEdit.fileType || "image/jpeg",
              lastModified: Date.now(),
            });
          } catch (fetchError) {
            console.error("Error fetching image for crop:", fetchError);
            toast.error(t("ErrorCroppingImage"));
            setCropModalOpen(false);
            setImageToEdit(null);
            return;
          }
        }

        if (!file) {
          console.log("[applyCrop] no file");

          toast.error(t("ErrorCroppingImage"));
          setCropModalOpen(false);
          setImageToEdit(null);
          return;
        }

        // Crop image using cropArea
        const croppedFile = await cropImage(file, activeCropArea);

        // EXACT COPY from rotatePhoto: Create blob from file and then URL from blob
        const croppedBlob = new Blob([croppedFile], { type: croppedFile.type });
        const preview = URL.createObjectURL(croppedBlob);

        // Free old URL only if it's a blob URL - EXACT COPY from rotatePhoto
        if (imageToEdit.preview.startsWith("blob:")) {
          URL.revokeObjectURL(imageToEdit.preview);
        }

        let updatedPhoto: PhotoItem;

        if (autoSerialize) {
          console.log("[applyCrop] autoSerialize=true, processing fileData");
          // If serialization is used, update serialized data
          const fileData = await readFileAsBase64(croppedFile);
          updatedPhoto = {
            ...imageToEdit,
            file: croppedFile,
            fileName: croppedFile.name,
            fileType: croppedFile.type,
            fileSize: croppedFile.size,
            fileData,
            preview,
            blobPreview: preview, // Set blobPreview for consistency
          };

          // Update photos in autoSerialize mode - THIS WAS MISSING!
          const updatedPhotos = displayPhotos.map((p) =>
            p.id === imageToEdit.id ? updatedPhoto : p,
          );
          console.log(
            "[applyCrop] autoSerialize onChange called, updatedPhoto:",
            {
              id: updatedPhoto.id,
              preview: updatedPhoto.preview,
              hasFileData: !!updatedPhoto.fileData,
            },
          );
          onChange(updatedPhotos);
        } else {
          console.log("[applyCrop] autoSerialize=false, uploading to server");
          // Use immediate upload for cropped image
          updatedPhoto = {
            ...imageToEdit,
            file: croppedFile,
            preview,
            blobPreview: preview, // Set blobPreview for cropped image
            uploadStatus: "uploading" as const,
            uploadProgress: 0,
          };

          // Update photo list with uploading status first
          const photosWithUploading = displayPhotos.map((p) =>
            p.id === imageToEdit.id ? updatedPhoto : p,
          );
          // onChange(photosWithUploading);

          // Start immediate upload of cropped image
          try {
            // Update with uploading status first
            // onChange(photosWithUploading);

            await imageUploadService.uploadImage(croppedFile, {
              onProgress: (progress) => {
                // Get current photos from value prop and update with progress
                const currentPhotos = value || [];
                const progressPhotos = currentPhotos.map((photo: PhotoItem) =>
                  photo.id === imageToEdit.id
                    ? { ...photo, uploadProgress: progress.percentage }
                    : photo,
                );
                onChange(progressPhotos);
              },
              onSuccess: (result) => {
                if (preview.startsWith("blob:")) {
                  URL.revokeObjectURL(preview);
                }

                commitPhotos((prev) => {
                  console.log(
                    "Updating photos after crop upload success:",
                    imageToEdit,
                    prev,
                  );
                  return prev.map((p) =>
                    p.id === imageToEdit.id
                      ? {
                          ...p,
                          id: result.uuid,
                          tempId: result.uuid,
                          uuid: result.uuid,
                          populated: undefined,
                          url: result.url,
                          preview: result.url, // Use server URL as preview
                          uploadStatus: "success" as const,
                          uploadProgress: 100,
                          // Keep the cropped file for potential future operations
                          file: croppedFile,
                          fileName: croppedFile.name,
                          fileType: croppedFile.type,
                          fileSize: croppedFile.size,
                          // Clear old blobPreview since we have server URL now
                          blobPreview: undefined,
                        }
                      : p,
                  );
                });
              },
              onError: (error) => {
                // Use photosRef to get current state and update
                const currentPhotos = photosRef.current;
                const failedPhoto = currentPhotos.find(
                  (p) => p.id === imageToEdit.id,
                );
                if (failedPhoto) {
                  cleanupPhotoUrls(failedPhoto);
                }
                const filtered = currentPhotos.filter(
                  (p) => p.id !== imageToEdit.id,
                );
                onChange(filtered);
                const errMsg =
                  typeof error === "object" &&
                  error &&
                  "message" in (error as any)
                    ? (error as any).message
                    : "Upload failed";
                toast.error(t(errMsg));
              },
            });
          } catch (uploadError) {
            // Handle upload error
            const errorPhotos = displayPhotos.map((p) =>
              p.id === imageToEdit.id
                ? {
                    ...updatedPhoto,
                    uploadStatus: "error" as const,
                    error:
                      uploadError instanceof Error
                        ? uploadError.message
                        : "Upload failed",
                  }
                : p,
            );
            onChange(errorPhotos);
          }
        }

        // Close modal window and reset state
        setCropModalOpen(false);
        setImageToEdit(null);
      } catch (error) {
        console.error("Error cropping image:", error);
        toast.error(t("ErrorCroppingImage"));
        setCropModalOpen(false);
        setImageToEdit(null);
      }
    },
    [imageToEdit, cropArea, displayPhotos, onChange, t, autoSerialize, value],
  );

  // Open file selection dialog
  const openMainFileDialog = () => {
    if (mainInputRef.current) {
      mainInputRef.current.click();
    }
  };

  const openAdditionalFileDialog = (index: number) => {
    if (additionalInputRefs[index].current) {
      additionalInputRefs[index].current.click();
    }
  };

  // Correct the logic for displaying placeholders with memoization
  const placeholders = useMemo(
    () =>
      Array(placeholdersCount)
        .fill(0)
        .map((_, index) => {
          const photo = additionalPhotos[index];
          return { photo, index };
        }),
    [additionalPhotos, placeholdersCount],
  );

  // Adjust the condition for displaying extra placeholders
  const showExtraPlaceholders = displayPhotos.length > placeholdersCount;

  // Calculate the number of extra placeholder rows to show
  const extraPlaceholderRowsCount = showExtraPlaceholders
    ? Math.ceil((displayPhotos.length - placeholdersCount) / 5)
    : 0;

  // Photo menu component
  const PhotoMenu: React.FC<{ photoId: string }> = React.memo(({ photoId }) => {
    const photo = displayPhotos.find((p) => p.id === photoId);
    const isMain = photo?.isMain || false;

    return (
      <div
        className={cn(
          "absolute bottom-4 left-1/2 -translate-x-1/2 hidden",
          "group-hover:flex",
          styles.photoMenu,
        )}
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
      >
        {!isMain ? (
          <button
            type="button"
            className={cn(styles.menuButton, {
              "bg-[#FF4B55]": isMain,
            })}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setAsMainPhoto(photoId);
            }}
            title={isMain ? t("Main photo") : t("Set as main")}
            aria-label={isMain ? t("Main photo") : t("Set as main")}
            disabled={isMain}
          >
            <StarIconRed
              className={cn(styles["icon-default"], { "opacity-0": isMain })}
            />
            <StarIconWhite
              className={cn(styles["icon-hover"], {
                "opacity-100": isMain,
                "opacity-0 group-hover:opacity-100": !isMain,
              })}
            />
          </button>
        ) : null}
        <button
          type="button"
          className={cn(styles.menuButton)}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            viewPhoto(photoId);
          }}
          title={t("View")}
          aria-label={t("View")}
        >
          <ViewIconRed className={cn(styles["icon-default"])} />
          <ViewIconWhite
            className={cn(
              styles["icon-hover"],
              "opacity-0 group-hover:opacity-100",
            )}
          />
        </button>
        <button
          type="button"
          className={cn(styles.menuButton)}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            rotatePhoto(photoId);
          }}
          title={t("Rotate")}
          aria-label={t("Rotate")}
        >
          <RotateIconRed className={cn(styles["icon-default"])} />
          <RotateIconWhite
            className={cn(
              styles["icon-hover"],
              "opacity-0 group-hover:opacity-100",
            )}
          />
        </button>
        <button
          type="button"
          className={cn(styles.menuButton)}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            cropPhoto(photoId);
          }}
          title={t("Crop")}
          aria-label={t("Crop")}
        >
          <CropIconRed className={cn(styles["icon-default"])} />
          <CropIconWhite
            className={cn(
              styles["icon-hover"],
              "opacity-0 group-hover:opacity-100",
            )}
          />
        </button>
        <button
          type="button"
          className={cn(styles.menuButton)}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            removePhoto(photoId);
          }}
          title={t("Delete")}
          aria-label={t("Delete")}
        >
          <DeleteIconRed className={cn(styles["icon-default"])} />
          <DeleteIconWhite
            className={cn(
              styles["icon-hover"],
              "opacity-0 group-hover:opacity-100",
            )}
          />
        </button>
      </div>
    );
  });

  PhotoMenu.displayName = "PhotoMenu";

  // Add wrapper for photo with double click
  interface PhotoWrapperProps {
    children: React.ReactNode;
    photoId: string;
    onDoubleClick: (photoId: string) => void;
  }

  const PhotoWrapper: React.FC<PhotoWrapperProps> = React.memo(
    ({ children, photoId, onDoubleClick }) => {
      const handleDoubleClick = useCallback(
        (e: React.MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          if (photoId) {
            onDoubleClick(photoId);
          }
        },
        [photoId, onDoubleClick],
      );

      return (
        <div
          className={styles.photoWrapper}
          onDoubleClick={handleDoubleClick}
        >
          {children}
        </div>
      );
    },
    (prevProps, nextProps) => {
      return prevProps.photoId === nextProps.photoId;
    },
  );

  PhotoWrapper.displayName = "PhotoWrapper";

  // Update SortablePhoto component with memoization
  interface SortablePhotoProps {
    photo: PhotoItem;
    index: number;
    children: React.ReactNode;
  }

  const SortablePhoto: React.FC<SortablePhotoProps> = React.memo(
    ({ photo, index, children }) => {
      const { attributes, listeners, setNodeRef, transform, transition } =
        useSortable({
          id: photo.id,
          disabled: false,
        });

      const style = {
        transform: CSS.Transform.toString(transform),
        transition,
      };

      return (
        <div
          ref={setNodeRef}
          style={style}
          {...attributes}
          {...listeners}
          className={styles.draggablePhoto}
          data-photo-id={photo.id}
        >
          {children}
        </div>
      );
    },
    (prevProps, nextProps) => {
      // Custom comparison function to prevent unnecessary re-renders
      // EXCLUDE uploadProgress to prevent re-renders during upload progress updates
      return (
        prevProps.photo.id === nextProps.photo.id &&
        prevProps.photo.preview === nextProps.photo.preview &&
        prevProps.photo.uploadStatus === nextProps.photo.uploadStatus &&
        // prevProps.photo.uploadProgress === nextProps.photo.uploadProgress && // ❌ REMOVED - causes re-renders
        prevProps.photo.isMain === nextProps.photo.isMain &&
        prevProps.index === nextProps.index
      );
    },
  );

  SortablePhoto.displayName = "SortablePhoto";

  // Add functions for photo processing with stable callbacks
  const handleDoubleClick = useCallback(
    (photoId: string) => {
      const photo = displayPhotos.find((p) => p.id === photoId);
      if (!photo) {
        return;
      }

      // Update isMain for all photos, making selected photo main
      const updatedPhotos = displayPhotos.map((p) => ({
        ...p,
        isMain: p.id === photoId,
        isPrimary: p.id === photoId,
      }));

      onChange(updatedPhotos);
    },
    [displayPhotos, onChange],
  );

  // Add new state for crop position
  const [cropPosition, setCropPosition] = useState({ x: 0, y: 0 });
  const MOVE_STEP = 10; // pixels to move on each arrow press

  // Add keyboard handler
  const handleCropKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!cropModalOpen) {
        return;
      }

      switch (e.key) {
        case "ArrowLeft":
          setCropPosition((prev) => ({ ...prev, x: prev.x - MOVE_STEP }));
          break;
        case "ArrowRight":
          setCropPosition((prev) => ({ ...prev, x: prev.x + MOVE_STEP }));
          break;
        case "ArrowUp":
          setCropPosition((prev) => ({ ...prev, y: prev.y - MOVE_STEP }));
          break;
        case "ArrowDown":
          setCropPosition((prev) => ({ ...prev, y: prev.y + MOVE_STEP }));
          break;
      }
    },
    [cropModalOpen],
  );

  // Add effect for keyboard events
  useEffect(() => {
    if (cropModalOpen) {
      window.addEventListener("keydown", handleCropKeyDown);
      return () => window.removeEventListener("keydown", handleCropKeyDown);
    }
  }, [cropModalOpen, handleCropKeyDown]);

  // Add handlers for control buttons
  const moveCrop = useCallback(
    (direction: "left" | "right" | "up" | "down") => {
      switch (direction) {
        case "left":
          setCropPosition((prev) => ({ ...prev, x: prev.x - MOVE_STEP }));
          break;
        case "right":
          setCropPosition((prev) => ({ ...prev, x: prev.x + MOVE_STEP }));
          break;
        case "up":
          setCropPosition((prev) => ({ ...prev, y: prev.y - MOVE_STEP }));
          break;
        case "down":
          setCropPosition((prev) => ({ ...prev, y: prev.y + MOVE_STEP }));
          break;
      }
    },
    [],
  );

  // Update dropzone settings
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptedTypes
      .split(",")
      .reduce((acc, type) => ({ ...acc, [type.trim()]: [] }), {}),
    multiple: true,
    noClick: true, // Disable click on entire zone
    noKeyboard: true, // Disable keyboard handling for entire zone
  });

  // Generate unique ID for photo
  const generatePhotoId = (): string => {
    return `photo-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };

  // Separate component for upload progress to prevent re-renders
  interface UploadProgressProps {
    uploadStatus?: PhotoItem["uploadStatus"];
    uploadProgress?: number;
    error?: string;
    photoId: string;
  }

  const UploadProgress: React.FC<UploadProgressProps> = React.memo(
    ({ uploadStatus, uploadProgress, error, photoId }) => {
      console.log("[UploadProgress] Rendering for photo", photoId, {
        uploadStatus,
        uploadProgress,
      });

      if (uploadStatus === "uploading") {
        return (
          <div className={styles.uploadProgress}>
            <div className={styles.progressBar}>
              <div
                className={styles.progressFill}
                style={{ width: `${uploadProgress || 0}%` }}
              />
            </div>
            <div className={styles.progressText}>
              Uploading... {uploadProgress || 0}%
            </div>
          </div>
        );
      }

      if (uploadStatus === "error") {
        return (
          <div className={styles.uploadProgress}>
            <div className={styles.progressText}>Upload failed: {error}</div>
            <button
              className={styles.retryButton}
              onClick={(e) => {
                e.stopPropagation();
                console.log("Retry upload for photo:", photoId);
              }}
            >
              Retry
            </button>
          </div>
        );
      }

      return null;
    },
    (prevProps, nextProps) => {
      // Only re-render if upload-related props change
      return (
        prevProps.uploadStatus === nextProps.uploadStatus &&
        prevProps.uploadProgress === nextProps.uploadProgress &&
        prevProps.error === nextProps.error &&
        prevProps.photoId === nextProps.photoId
      );
    },
  );

  UploadProgress.displayName = "UploadProgress";

  // Show toast notification when any photo has an upload error
  const lastErrorRef = useRef<string | null>(null);
  useEffect(() => {
    const errored = displayPhotos.find(
      (p) =>
        p.uploadStatus === "error" &&
        p.error &&
        p.error !== lastErrorRef.current,
    );

    if (errored?.error) {
      toast.error(t(errored.error));
      lastErrorRef.current = errored.error;
    }
  }, [displayPhotos]);

  // --- Async image processing integration ---
  const {
    status: processingStatus,
    completedImages,
    start: startQueueProcessing,
  } = useFormImageQueue({
    onComplete: (imgs) => {
      // Replace temp URLs with final URLs in the same order
      const currentPhotos = value || [];
      const updated = currentPhotos.map((p, idx) => {
        if (p.uploadStatus === "success" && imgs[idx]) {
          return { ...p, url: imgs[idx], preview: imgs[idx] };
        }
        return p;
      });
      onChange(updated);
    },
  });

  useEffect(() => {
    if (processingStatus !== "idle") return;

    const allUploaded = (value || []).every(
      (p) => p.uploadStatus === "success" && p.url,
    );

    if (allUploaded && (value || []).length) {
      const urls = (value || []).map((p) => p.url!) as string[];
      startQueueProcessing(urls);
    }
  }, [value, processingStatus, startQueueProcessing]);

  return (
    <div
      className={cn("w-full flex flex-col gap-4", styles.container, className)}
    >
      {label && (
        <div
          className={cn(
            "text-base font-medium mb-2 text-gray-910",
            styles.title,
          )}
        >
          {label}
          {required && <span className="text-red ml-1">*</span>}
        </div>
      )}

      {description && (
        <div
          className={cn(
            "text-sm text-gray-600 mb-4 leading-normal",
            styles.description,
          )}
        >
          {description}
        </div>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div
          {...getRootProps()}
          className={cn(
            "grid grid-cols-2 sm:grid-cols-5 gap-4 w-full mb-4 border-transparent border-2",
            styles.photoGrid,
            isDragActive && "border-dashed border-red bg-red-50",
          )}
        >
          <input {...getInputProps()} />

          {/* Main photo */}
          <div
            className={cn(
              "col-span-2 row-span-2 relative aspect-square rounded-xl bg-gray-100",
              "bg-[url('data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='20' ry='20' stroke='%23BABABA' stroke-width='2' stroke-dasharray='12, 8' stroke-dashoffset='0' stroke-linecap='round'/%3e%3c/svg%3e')]",
              "flex flex-col items-center justify-center cursor-pointer transition-all duration-200",
              "shadow-[0_0.25rem_1.25rem_rgba(0,0,0,0.1)] overflow-hidden",
              styles.mainPhotoContainer,
              {
                [styles.uploading]: mainPhoto?.uploadStatus === "uploading",
                [styles.error]: mainPhoto?.uploadStatus === "error",
              },
              isDragActive && "pointer-events-none",
            )}
          >
            {mainPhoto ? (
              <PhotoWrapper
                photoId={mainPhoto.id}
                onDoubleClick={handleDoubleClick}
              >
                <LoggedSortableContext
                  items={mainPhotoSortableItems} // Use memoized items
                  strategy={sortingStrategy}
                  contextName="mainPhoto"
                >
                  <SortablePhoto
                    photo={mainPhoto}
                    index={0}
                  >
                    <OptimizedImage
                      blobSrc={mainPhoto.blobPreview}
                      serverSrc={mainPhoto.url}
                      alt={t("MainPhoto")}
                      className="object-cover"
                      fill
                      sizes="(max-width: 768px) 100dvw, (max-width: 1200px) 50vw, 33vw"
                      uploadStatus={mainPhoto.uploadStatus}
                      enableTransition={true}
                      imageSize="large"
                    />
                    <UploadProgress
                      uploadStatus={mainPhoto.uploadStatus}
                      uploadProgress={mainPhoto.uploadProgress}
                      error={mainPhoto.error}
                      photoId={mainPhoto.id}
                    />
                    <PhotoMenu photoId={mainPhoto.id} />
                  </SortablePhoto>
                </LoggedSortableContext>
              </PhotoWrapper>
            ) : (
              <div
                onClick={openMainFileDialog}
                role="button"
                tabIndex={0}
                aria-label={mainPhotoLabel}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    openMainFileDialog();
                  }
                }}
                className="flex flex-col items-center justify-center w-full h-full"
              >
                <span
                  className={cn(
                    "flex items-center justify-center mb-5",
                    styles.navIcon,
                  )}
                >
                  <PhotoUploadIcon
                    className={cn("w-16 h-16", styles["icon-default"])}
                  />
                  <PhotoUploadIconRed
                    className={cn("w-16 h-16", styles["icon-red"])}
                  />
                </span>
                <div className="text-base text-gray-910 text-center px-4 font-normal leading-normal">
                  {mainPhotoLabel}
                </div>
              </div>
            )}
            <input
              ref={mainInputRef}
              type="file"
              accept={acceptedTypes}
              onChange={handleMainPhotoUpload}
              className="hidden"
              aria-hidden="true"
              multiple
            />
          </div>

          {/* Additional photos in main grid */}
          <div
            className={cn("contents", isDragActive && "pointer-events-none")}
          >
            <LoggedSortableContext
              items={additionalPhotosSortableItems} // Use memoized items
              strategy={sortingStrategy}
              contextName="additionalPhotos"
            >
              {placeholders.map(({ photo, index }) => (
                <div
                  key={photo ? photo.id : `placeholder-${index}`}
                  className={cn(
                    "relative aspect-square rounded-xl bg-gray-100",
                    "bg-[url('data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='20' ry='20' stroke='%23BABABA' stroke-width='2' stroke-dasharray='12, 8' stroke-dashoffset='0' stroke-linecap='round'/%3e%3c/svg%3e')]",
                    "flex flex-col items-center justify-center cursor-pointer transition-all duration-200",
                    "shadow-[0_0.25rem_1.25rem_rgba(0,0,0,0.1)] overflow-hidden",
                    styles.photoContainer,
                    {
                      [styles.uploading]: photo?.uploadStatus === "uploading",
                      [styles.error]: photo?.uploadStatus === "error",
                    },
                  )}
                >
                  {photo ? (
                    <PhotoWrapper
                      photoId={photo.id}
                      onDoubleClick={handleDoubleClick}
                    >
                      <SortablePhoto
                        photo={photo}
                        index={index}
                      >
                        <OptimizedImage
                          blobSrc={photo.blobPreview}
                          serverSrc={photo.url}
                          alt={t("Photo")}
                          className="object-cover"
                          fill
                          sizes="(max-width: 768px) 100dvw, (max-width: 1200px) 50vw, 33vw"
                          uploadStatus={photo.uploadStatus}
                        />
                        <UploadProgress
                          uploadStatus={photo.uploadStatus}
                          uploadProgress={photo.uploadProgress}
                          error={photo.error}
                          photoId={photo.id}
                        />
                        <PhotoMenu photoId={photo.id} />
                      </SortablePhoto>
                    </PhotoWrapper>
                  ) : (
                    <div
                      onClick={() => openAdditionalFileDialog(index)}
                      role="button"
                      tabIndex={0}
                      aria-label={additionalPhotoLabel}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          openAdditionalFileDialog(index);
                        }
                      }}
                      className="flex flex-col items-center justify-center w-full h-full"
                    >
                      <span
                        className={cn(
                          "flex items-center justify-center mb-4",
                          styles.navIcon,
                        )}
                      >
                        <PhotoUploadSmallIcon
                          className={cn("w-10 h-10", styles["icon-default"])}
                        />
                        <PhotoUploadSmallIconRed
                          className={cn("w-10 h-10", styles["icon-red"])}
                        />
                      </span>
                      <div className="text-sm text-gray-600 text-center px-2 font-normal leading-snug max-w-[10rem]">
                        {additionalPhotoLabel}
                      </div>
                    </div>
                  )}
                  <input
                    ref={additionalInputRefs[index]}
                    type="file"
                    accept={acceptedTypes}
                    onChange={handleAdditionalPhotoUpload}
                    className="hidden"
                    aria-hidden="true"
                    multiple
                  />
                </div>
              ))}
            </LoggedSortableContext>
          </div>

          {/* Update the rendering logic for extra photos and placeholders */}
          {showExtraPlaceholders && (
            <div
              className={cn(
                styles.extraPhotosContainer,
                isDragActive && "pointer-events-none",
              )}
            >
              <LoggedSortableContext
                items={extraPhotosSortableItems} // Use memoized items
                strategy={sortingStrategy}
                contextName="extraPhotos"
              >
                {/* Display additional photos that do not fit in the main grid */}
                {additionalPhotos
                  .slice(placeholdersCount)
                  .map((photo, index) => (
                    <div
                      key={photo.id}
                      className={styles.photoContainer}
                    >
                      <PhotoWrapper
                        photoId={photo.id}
                        onDoubleClick={handleDoubleClick}
                      >
                        <SortablePhoto
                          photo={photo}
                          index={index + placeholdersCount}
                        >
                          <OptimizedImage
                            blobSrc={photo.blobPreview}
                            serverSrc={photo.url}
                            alt={t("Photo")}
                            className={styles.photoPreview}
                            fill
                            sizes="(max-width: 768px) 100dvw, (max-width: 1200px) 50vw, 33vw"
                            uploadStatus={photo.uploadStatus}
                          />
                          <PhotoMenu photoId={photo.id} />
                        </SortablePhoto>
                      </PhotoWrapper>
                    </div>
                  ))}

                {/* Additional placeholders if total photos are less than maxPhotos */}
                {Array.from({
                  length: Math.max(
                    0,
                    extraPlaceholderRowsCount * 5 -
                      additionalPhotos.slice(placeholdersCount).length,
                  ),
                }).map((_, index) => {
                  // Calculate the index for the additionalInputRefs array
                  const inputIndex =
                    placeholdersCount +
                    additionalPhotos.slice(placeholdersCount).length +
                    index;
                  // Break if we exceed maxPhotos or placeholders count
                  if (inputIndex >= maxPhotos) {
                    return null;
                  }

                  // Don't render placeholder if max photos limit is reached
                  if (displayPhotos.length >= maxPhotos) {
                    return null;
                  }

                  return (
                    <div
                      key={`placeholder-extra-${index}`}
                      className={styles.photoContainer}
                    >
                      <div
                        onClick={() => openAdditionalFileDialog(inputIndex)}
                        role="button"
                        tabIndex={0}
                        aria-label={additionalPhotoLabel}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            openAdditionalFileDialog(inputIndex);
                          }
                        }}
                      >
                        <span className={styles.navIcon}>
                          <PhotoUploadSmallIcon
                            className={cn(
                              styles["icon-default"],
                              "h-[4rem] w-[4rem]",
                            )}
                          />
                          <PhotoUploadSmallIconRed
                            className={cn(
                              styles["icon-red"],
                              "h-[4rem] w-[4rem]",
                            )}
                          />
                        </span>
                        <div className={styles.smallUploadText}>
                          {additionalPhotoLabel}
                        </div>
                      </div>
                      <input
                        ref={additionalInputRefs[inputIndex]}
                        type="file"
                        accept={acceptedTypes}
                        onChange={handleAdditionalPhotoUpload}
                        className="hidden"
                        aria-hidden="true"
                        multiple
                      />
                    </div>
                  );
                })}
              </LoggedSortableContext>
            </div>
          )}
        </div>

        <DragOverlay>
          {activeId ? (
            <div
              className={cn(
                "relative w-full h-full cursor-grab active:cursor-grabbing",
                styles.draggablePhoto,
              )}
            >
              <OptimizedImage
                blobSrc={
                  displayPhotos.find((photo) => photo.id === activeId)
                    ?.blobPreview || ""
                }
                serverSrc={
                  displayPhotos.find((photo) => photo.id === activeId)?.url ||
                  ""
                }
                alt={t("DraggingPhoto")}
                className="object-cover"
                fill
                sizes="(max-width: 768px) 100dvw, (max-width: 1200px) 50vw, 33vw"
              />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {error && (
        <div className={cn("text-sm text-red mt-1", styles.error)}>{error}</div>
      )}

      {/* Modal for image cropping */}
      <CropModal
        isOpen={cropModalOpen && !!imageToEdit}
        cropFile={imageToEdit}
        setCropFile={(file) => {
          setImageToEdit(file);
          if (!file) {
            setCropModalOpen(false);
          }
        }}
        cropIndex={cropPhotoIndex}
        handleAddPhoto={(files, index) => {
          if (index !== undefined) {
            const updatedPhotos = [...displayPhotos];
            const newPhoto = {
              id: updatedPhotos[index]?.id || generatePhotoId(),
              file: files[0],
              preview: URL.createObjectURL(files[0]),
              isMain: updatedPhotos[index]?.isMain || false,
            };
            updatedPhotos[index] = newPhoto;
            onChange(updatedPhotos);
          } else {
            const newPhotos = files.map((file) => ({
              id: generatePhotoId(),
              file,
              preview: URL.createObjectURL(file),
              isMain: false,
            }));
            onChange([...displayPhotos, ...newPhotos]);
          }
          setCropModalOpen(false);
        }}
        displayPhotos={displayPhotos}
        onChange={onChange}
        setCropPhotoIndex={setCropPhotoIndex}
        onApply={async (cropAreaPixels) => {
          // Call applyCrop with the crop area directly
          await applyCrop(cropAreaPixels);
        }}
      />

      {/* Modal for image viewing */}
      {viewImageUrl && (
        <Modal onClose={() => setViewImageUrl(null)}>
          <div
            className={cn(
              "flex flex-col items-center p-5 max-w-[90vw] max-h-[90vh]",
              styles.viewImageContainer,
            )}
          >
            <div
              className={cn(
                "flex justify-center items-center w-full h-full max-h-[80vh] overflow-auto mb-5",
                styles.viewImageWrapper,
              )}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={viewImageUrl}
                alt={t("FullSizeImage")}
                className={cn(
                  "max-w-full max-h-full object-contain",
                  styles.fullSizeImage,
                )}
              />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

/**
 * Rotate image 90 degrees clockwise
 * @param file Image file for rotation
 * @returns Promise with rotated file
 */
const rotateImage = (file: File): Promise<File> => {
  if (!isBrowser) {
    return Promise.reject(new Error("Browser API not available"));
  }

  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => {
      // Create canvas and set its dimensions
      const canvas = document.createElement("canvas");
      // When rotating 90 degrees, width and height swap
      canvas.width = img.height;
      canvas.height = img.width;

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Unable to get canvas context"));
        return;
      }

      // Rotate image 90 degrees clockwise
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(Math.PI / 2); // 90 degrees in radians
      ctx.drawImage(img, -img.width / 2, -img.height / 2);

      // Convert canvas to blob and create new File
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error("Unable to convert canvas to blob"));
          return;
        }

        // Create new file with the same name and type
        const rotatedFile = new File([blob], file.name, {
          type: file.type,
          lastModified: Date.now(),
        });

        resolve(rotatedFile);
      }, file.type);
    };

    img.onerror = () => {
      reject(new Error("Unable to load image"));
    };

    // Load image from File
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Crop image based on specified area
 * @param file Image file for cropping
 * @param cropArea Cropping area {x, y, width, height}
 * @returns Promise with cropped file
 */
const cropImage = (
  file: File,
  cropArea: { x: number; y: number; width: number; height: number },
): Promise<File> => {
  if (!isBrowser) {
    return Promise.reject(new Error("Browser API not available"));
  }

  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => {
      // Create canvas and set its dimensions
      const canvas = document.createElement("canvas");
      canvas.width = cropArea.width;
      canvas.height = cropArea.height;

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Unable to get canvas context"));
        return;
      }

      // Draw only the needed part of the image
      ctx.drawImage(
        img,
        cropArea.x,
        cropArea.y,
        cropArea.width,
        cropArea.height,
        0,
        0,
        cropArea.width,
        cropArea.height,
      );

      // Convert canvas to blob and create new File
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error("Unable to convert canvas to blob"));
          return;
        }

        try {
          // Create new file with the same name and type
          const croppedFile = new File(
            [blob],
            file.name || "cropped-image.jpg",
            {
              type: file.type || "image/jpeg",
              lastModified: Date.now(),
            },
          );

          resolve(croppedFile);
        } catch (error) {
          reject(new Error(`Failed to create File object: ${error}`));
        }
      }, file.type || "image/jpeg");
    };

    img.onerror = (err) => {
      reject(new Error(`Unable to load image: ${err}`));
    };

    try {
      // Load image from File
      img.src = URL.createObjectURL(file);
    } catch (err) {
      reject(new Error(`Failed to create object URL: ${err}`));
    }
  });
};

/**
 * Serialize PhotoItem in format that can be saved in JSON
 * @param photos Array of PhotoItem to serialize
 * @returns Promise with array of serialized photos
 */
export const serializePhotos = async (
  photos: PhotoItem[],
): Promise<PhotoItem[]> => {
  const serializedPhotos: PhotoItem[] = [];

  for (const photo of photos) {
    // If photo has server URL (successfully uploaded), save server data only
    if (photo.tempId && photo.url && photo.uploadStatus === "success") {
      serializedPhotos.push({
        id: photo.id,
        tempId: photo.tempId,
        url: photo.url,
        preview: photo.url, // Use server URL as preview
        fileName: photo.fileName || "",
        fileType: photo.fileType || "",
        fileSize: photo.fileSize || 0,
        uploadStatus: photo.uploadStatus,
        uploadProgress: photo.uploadProgress,
        isMain: photo.isMain,
      });
      continue;
    }

    // Do NOT save files that are not uploaded - skip them
    // This prevents saving large base64 data in IndexDB
    if (photo.file && (!photo.url || photo.uploadStatus !== "success")) {
      // Skip non-uploaded files - they should be re-uploaded
      continue;
    }

    // If this is already a serialized photo with URL, keep it
    if (photo.url && !photo.file) {
      serializedPhotos.push({
        id: photo.id,
        url: photo.url,
        preview: photo.url,
        fileName: photo.fileName || "",
        fileType: photo.fileType || "",
        fileSize: photo.fileSize || 0,
        isMain: photo.isMain,
        tempId: photo.tempId,
        uploadStatus: "success", // Mark as success if we have URL
      });
    }
  }

  return serializedPhotos;
};

/**
 * Deserialize photos from JSON to PhotoItem format
 * @param serializedPhotos Array of serialized photos
 * @returns Array of PhotoItem, ready to use in component
 */
export const deserializePhotos = (
  serializedPhotos: PhotoItem[],
): PhotoItem[] => {
  return serializedPhotos
    .map((photo) => {
      // If photo has server URL (was successfully uploaded), restore server data
      if (photo.url) {
        return {
          ...photo,
          preview: photo.url, // Use server URL as preview
          uploadStatus: photo.uploadStatus || "success",
        } as PhotoItem;
      }

      // Skip photos without URL - they should be re-uploaded
      return null;
    })
    .filter((photo): photo is PhotoItem => photo !== null);
};

/**
 * Convert File to base64 string
 * @param file File for reading
 * @returns Promise with base64 string
 */
const readFileAsBase64 = (file: File): Promise<string> => {
  if (!isBrowser) {
    return Promise.reject(new Error("Browser API not available"));
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      resolve(reader.result as string);
    };

    reader.onerror = () => {
      reject(new Error("File reading error"));
    };

    reader.readAsDataURL(file);
  });
};

// Export memoized component to prevent re-renders when parent re-renders
export default React.memo(FormPhotoUpload, (prevProps, nextProps) => {
  // Smart comparison for value array - ignore uploadProgress changes
  const prev = prevProps.value || [];
  const next = nextProps.value || [];

  let valueChanged = false;

  if (prev.length !== next.length) {
    valueChanged = true;
  } else {
    // Compare each photo but ignore uploadProgress
    for (let i = 0; i < prev.length; i++) {
      const prevPhoto = prev[i];
      const nextPhoto = next[i];

      if (
        prevPhoto.id !== nextPhoto.id ||
        prevPhoto.uploadStatus !== nextPhoto.uploadStatus ||
        prevPhoto.url !== nextPhoto.url ||
        prevPhoto.isMain !== nextPhoto.isMain ||
        prevPhoto.preview !== nextPhoto.preview
      ) {
        valueChanged = true;
        break; // Important change detected
      }
    }
  }

  // Check other props that might affect rendering
  const propsChanged =
    prevProps.error !== nextProps.error ||
    prevProps.maxPhotos !== nextProps.maxPhotos ||
    prevProps.required !== nextProps.required ||
    prevProps.label !== nextProps.label ||
    prevProps.description !== nextProps.description ||
    prevProps.mainPhotoLabel !== nextProps.mainPhotoLabel ||
    prevProps.additionalPhotoLabel !== nextProps.additionalPhotoLabel ||
    prevProps.placeholdersCount !== nextProps.placeholdersCount ||
    prevProps.autoSerialize !== nextProps.autoSerialize ||
    prevProps.className !== nextProps.className ||
    JSON.stringify(prevProps.mainPhotoSize) !==
      JSON.stringify(nextProps.mainPhotoSize) ||
    JSON.stringify(prevProps.additionalPhotoSize) !==
      JSON.stringify(nextProps.additionalPhotoSize);

  // Check if uploadProgress changed but was ignored
  const uploadProgressIgnored =
    !valueChanged &&
    prev.some((p, i) => p.uploadProgress !== next[i]?.uploadProgress);

  console.log("[FormPhotoUpload]  React.memo comparison", {
    valueChanged,
    propsChanged,
    willRerender: valueChanged || propsChanged,
    valueLength: prev.length,
    nextValueLength: next.length,
    uploadProgressIgnored,
  });

  // Return true if props are equal (should NOT re-render), false if different (should re-render)
  return !(valueChanged || propsChanged);
});
